import { LoaderSpinner, Modal } from '@lightspeed/design-system-react'
import { ApiProduct, ApiRetailerPaymentType, GiftCard } from '@vend/types'
import React, { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { OmniGiftCardSettings } from '../../../controllers/gift-cards/balances-gift-cards'
import { useRetrieveRetailerGiftCardSettings } from '../hooks'
import { SalePayment } from '../../sale/payment-panel/PaymentModal/types'
import { getFeatureFromCache } from '../../../util/feature-cache'
import { GiftCardSearch } from './GiftCardSearch'
import { ServerError } from './ServerError'
import { SetValue } from './SetValue'
import { PaymentState, ViewDetails } from './ViewDetails'

// Migrated from workflows in wr-gift-cards.ts
export type GiftCardModalFlow =
  | 'SELLING' // For selling a new gift card
  | 'REDEEMING' // For redeeming an existing gift card to pay for a sale

// Migrated from states in wr-gift-cards.ts
export type GiftCardModalState =
  | 'SEARCH'
  | 'SET_VALUE'
  | 'VIEW_DETAILS'
  | 'ADD_BALANCE'
  | 'SERVER_ERROR'
  | 'LOADING'

// Migrated from GIFTCARD_PROVIDER_SETTINGS
export type GiftCardType =
  | 'BALANCES' // X-series gift card
  | 'BIGCOMMERCE' // BigCommerce gift certificate, only possible when redeeming

interface GiftCardModalProps {
  giftCardType: GiftCardType
  flow: GiftCardModalFlow
  onClose: () => void
}

interface GiftCardModalSellingProps extends GiftCardModalProps {
  flow: 'SELLING'
  product: ApiProduct
}

export interface GiftCardModalPaymentProps extends GiftCardModalProps {
  flow: 'REDEEMING'
  amount: string
  paymentType: ApiRetailerPaymentType
  onSuccess: (result: SalePayment) => void
}

const flowIsRedeeming = (
  props: GiftCardModalSellingProps | GiftCardModalPaymentProps
): props is GiftCardModalPaymentProps => props.flow === 'REDEEMING'

export const GiftCardModal: React.FC<
  GiftCardModalSellingProps | GiftCardModalPaymentProps
> = props => {
  const { giftCardType, flow, onClose } = props
  const [state, setState] = useState<GiftCardModalState>('SEARCH')
  const [
    giftCardSettings,
    setGiftCardSettings,
  ] = useState<OmniGiftCardSettings | null>(null)
  const [giftCard, setGiftCard] = useState<GiftCard | null>(null)
  const [isDismissible, setIsDismissible] = useState(true)
  const onError = useCallback(() => {
    setState('SERVER_ERROR')
  }, [])

  const hasOmniGiftCards = getFeatureFromCache('omni_gift_cards')

  const retrieveRetailerGiftCardSettings = useRetrieveRetailerGiftCardSettings(
    flow,
    onClose,
    onError
  )

  useEffect(() => {
    if (!hasOmniGiftCards) {
      return
    }
    const fetchSettings = async () => {
      const settings = await retrieveRetailerGiftCardSettings()
      setGiftCardSettings(settings)
    }

    fetchSettings()
  }, [hasOmniGiftCards, retrieveRetailerGiftCardSettings])

  /**
   * Callback to handle when paying with gift card / gift certs in progress
   */
  const onPayment = useCallback((paymentState: PaymentState) => {
    setIsDismissible(paymentState === 'completed')
  }, [])

  return createPortal(
    <Modal size="medium" onClose={onClose} dismissable={isDismissible}>
      {(() => {
        switch (state) {
          case 'SEARCH':
            return (
              <GiftCardSearch
                flow={flow}
                giftCardType={giftCardType}
                giftCardSettings={giftCardSettings}
                setGiftCard={setGiftCard}
                setState={setState}
                onClose={onClose}
                onError={onError}
              />
            )
          case 'SET_VALUE':
            return giftCard &&
              (giftCardSettings || !hasOmniGiftCards) &&
              'product' in props ? (
              <SetValue
                flow={flow}
                state={state}
                giftCard={giftCard}
                giftCardType={giftCardType}
                giftCardSettings={giftCardSettings}
                product={props.product}
                onClose={onClose}
              />
            ) : null // TODO: Consider using the error state here
          case 'VIEW_DETAILS': {
            if (!giftCard) {
              return null
            }

            if (flowIsRedeeming(props)) {
              return (
                <ViewDetails
                  flow="REDEEMING"
                  giftCardType={giftCardType}
                  giftCard={giftCard}
                  setGiftCard={setGiftCard}
                  setState={setState}
                  onClose={onClose}
                  onSuccess={props.onSuccess}
                  amount={props.amount}
                  paymentType={props.paymentType}
                  onPayment={onPayment}
                />
              )
            }

            return (
              <ViewDetails
                flow="SELLING"
                giftCardType={giftCardType}
                giftCard={giftCard}
                setGiftCard={setGiftCard}
                setState={setState}
                onClose={onClose}
              />
            )
          }
          case 'ADD_BALANCE':
            // If the omni gift card feature is enabled we require giftCardSettings
            // but if the feature is disabled there are no settings
            return giftCard &&
              (giftCardSettings || !hasOmniGiftCards) &&
              'product' in props ? (
              <SetValue
                flow={flow}
                state={state}
                giftCard={giftCard}
                giftCardType={giftCardType}
                giftCardSettings={giftCardSettings}
                product={props.product}
                onClose={onClose}
              />
            ) : null
          case 'SERVER_ERROR':
            return <ServerError onClose={onClose} />
          case 'LOADING':
            return (
              <div className="vd-flex vd-flex--justify-center vd-flex--align-center">
                <LoaderSpinner />
              </div>
            )
        }
      })()}
    </Modal>,
    document.body
  )
}
